import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import PortalUserPagedCriteria from '../Models/PortalUser/Lookups/PortalUserPagedCriteria';
import PortalUserLookup from '../Models/PortalUser/Lookups/PortalUserLookup';
import PortalUser from '../Models/PortalUser/PortalUser';
import PortalTermsAndConditions from '../Models/PortalUser/PortalTermsAndConditions';
import NewTermsAndConditionsCommand from '../Models/PortalUser/Commands/NewTermsAndConditionsCommand';

export interface IPortalUserApiClient {

    /**
     * Gets a paged list of portal users
     * @param pageRequest The filter criteria
     * @returns A list of PortalUsers
     */
    getPortalUsersPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<PortalUserPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<PortalUser>>>;

    /**
     * Gets a list of portal user lookups
     */
    getPortalUserLookups(adminPortalOnly:boolean): AxiosPromise<Array<Model.PlainObject<PortalUserLookup>>>;

    /**
     * Gets a portal user with the specified portal user id
     * @param portalUserId The portal user id of the portal user to fetch
     * @returns The  with the given id
     */
    getPortalUser(portalUserId: number): AxiosPromise<Model.PlainTrackedObject<PortalUser>>;

    /**
     * Creates a new portal user
     * @param portalUser The portal user to create
     * @returns The created
     */
    save(portalUser: Model.PartialPlainObject<PortalUser>): AxiosPromise;

    /**
     * Marks a portal user with a flag indicating whether the portal user will be deleted when authorised.
     * @param portalUserId The id of the Portal User to set the pending delete flag on.
     * @param isPendingDelete The flag indicating if the portal user will be deleted when authorised.
     * @returns A Task
     */
    setPendingDelete(portalUserId: number, isPendingDelete: boolean): AxiosPromise;

    /** 
     * Gets the terms and conditions, or a new instance if missing.
     */
    getTermsAndConditions(): AxiosPromise<Model.PlainTrackedObject<PortalTermsAndConditions>>;

    /** 
     * Saves new terms and conditions.
     */
    saveTermsAndConditionsAsync(command: Model.PartialPlainObject<NewTermsAndConditionsCommand>): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class PortalUserApiClient extends Data.ApiClientBase implements IPortalUserApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/portal-users`);
    }

    public getPortalUsersPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<PortalUserPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<PortalUser>>> {
        return this.axios.post(`${this.apiPath}/paged`, pageRequest);
    }

    public getPortalUserLookups(adminPortalOnly : boolean): AxiosPromise<Array<Model.PlainObject<PortalUserLookup>>> {
        return this.axios.get(`${this.apiPath}/lookups?adminPortalOnly=${adminPortalOnly}`);
    }

    public getPortalUser(portalUserId: number): AxiosPromise<Model.PlainTrackedObject<PortalUser>> {
        return this.axios.get(`${this.apiPath}/${portalUserId}`);
    }

    public save(portalUser: Model.PartialPlainObject<PortalUser>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/save`, portalUser);
    }

    public setPendingDelete(portalUserId: number, isPendingDelete: boolean): AxiosPromise {
        return this.axios.post(`${this.apiPath}/pending-delete/${portalUserId}/${isPendingDelete}`);
    }

    public getTermsAndConditions(): AxiosPromise<Model.PlainTrackedObject<PortalTermsAndConditions>> {
        return this.axios.get(`${this.apiPath}/terms-and-conditions`);
    }

    public saveTermsAndConditionsAsync(command: Model.PartialPlainObject<NewTermsAndConditionsCommand>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/terms-and-conditions`, command);
    }

    // Client only properties / methods
}